import { useEffect, useState } from 'react';
import type { StartingValueCorrectionPreview } from '../../types/inventoryStartingValue';
import { formatCurrency, formatPreviewQuantity } from '../../utils/formatCurrency';
import { parseStartingValueNumber } from '../../utils/inventoryStartingValueForm';

type StartingValueCostCorrectionModalProps = {
  isOpen: boolean;
  itemName: string;
  currentUnitCost: string;
  isPreviewing?: boolean;
  isConfirming?: boolean;
  preview: StartingValueCorrectionPreview | null;
  error?: string | null;
  onPreview: (unitCost: number) => void | Promise<void>;
  onConfirm: () => void | Promise<void>;
  onBack: () => void;
  onCancel: () => void;
};

const formatDelta = (value: string | number) => {
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n) || n === 0) return formatCurrency(0);
  const prefix = n > 0 ? '+' : '';
  return `${prefix}${formatCurrency(n)}`;
};

const StartingValueCostCorrectionModal = ({
  isOpen,
  itemName,
  currentUnitCost,
  isPreviewing = false,
  isConfirming = false,
  preview,
  error = null,
  onPreview,
  onConfirm,
  onBack,
  onCancel,
}: StartingValueCostCorrectionModalProps) => {
  const [unitCostInput, setUnitCostInput] = useState('');
  const [clientError, setClientError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setUnitCostInput('');
    setClientError(null);
  }, [isOpen, currentUnitCost]);

  if (!isOpen) return null;

  const busy = isPreviewing || isConfirming;
  const hasPreview = preview != null;
  const hasBlockers = (preview?.blockers.length ?? 0) > 0;
  const currentCostNum = parseStartingValueNumber(currentUnitCost);

  const handlePreview = () => {
    const next = parseStartingValueNumber(unitCostInput);
    if (unitCostInput.trim() === '' || !Number.isFinite(Number(unitCostInput))) {
      setClientError('Enter a valid unit cost.');
      return;
    }
    if (next < 0) {
      setClientError('Unit cost must be 0 or greater.');
      return;
    }
    if (next === currentCostNum) {
      setClientError('New unit cost must differ from the current cost.');
      return;
    }
    setClientError(null);
    void onPreview(next);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-12 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
        <h3 className="text-lg font-medium text-gray-900">Correct opening unit cost</h3>
        <p className="mt-1 text-sm text-gray-600">
          Adjust FIFO unit cost for {itemName}. Inventory, equity, and sold-invoice COGS will be
          updated.
        </p>

        {!hasPreview ? (
          <div className="mt-4 space-y-4">
            <div className="flex justify-between gap-4 text-sm">
              <span className="text-gray-600">Current unit cost</span>
              <span className="font-mono font-medium tabular-nums">
                {formatCurrency(currentUnitCost)}
              </span>
            </div>
            <div>
              <label
                htmlFor="starting-value-correction-unit-cost"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                New unit cost
              </label>
              <input
                id="starting-value-correction-unit-cost"
                type="number"
                min="0"
                step="0.01"
                value={unitCostInput}
                onChange={(e) => {
                  setUnitCostInput(e.target.value);
                  setClientError(null);
                }}
                disabled={busy}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-500"
                placeholder="0.00"
                autoFocus
              />
            </div>
            {(clientError || error) && (
              <p className="text-sm text-red-600">{clientError || error}</p>
            )}
            <div className="flex justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePreview}
                disabled={busy}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isPreviewing ? 'Previewing...' : 'Preview'}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4">
            {preview.blockers.length > 0 && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm font-medium text-red-800 mb-1">Cannot proceed</p>
                <ul className="list-disc list-inside text-sm text-red-700 space-y-0.5">
                  {preview.blockers.map((blocker) => (
                    <li key={blocker}>{blocker}</li>
                  ))}
                </ul>
              </div>
            )}

            {preview.warnings.length > 0 && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-medium text-amber-900 mb-1">Warnings</p>
                <ul className="list-disc list-inside text-sm text-amber-800 space-y-0.5">
                  {preview.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">Unit cost</span>
                <span className="font-medium text-gray-900 tabular-nums">
                  {formatCurrency(preview.old_unit_cost)} → {formatCurrency(preview.new_unit_cost)}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">Sold qty</span>
                <span className="font-mono tabular-nums">
                  {formatPreviewQuantity(preview.sold_qty)}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">Qty on hand</span>
                <span className="font-mono tabular-nums">
                  {formatPreviewQuantity(preview.qty_on_hand)}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">COGS change</span>
                <span className="font-medium tabular-nums">{formatDelta(preview.cogs_delta)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">Equity change</span>
                <span className="font-medium tabular-nums">{formatDelta(preview.equity_delta)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">Inventory change</span>
                <span className="font-medium tabular-nums">
                  {formatDelta(preview.inventory_delta)}
                </span>
              </div>
            </div>

            {preview.affected_invoices.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Affected invoices ({preview.affected_invoice_count})
                </p>
                <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {preview.affected_invoices.map((invoice) => (
                    <div
                      key={invoice.invoice_id}
                      className="flex justify-between gap-3 px-3 py-2 text-sm"
                    >
                      <span className="text-gray-700">{invoice.invoice_number}</span>
                      <span className="tabular-nums font-medium">
                        {formatDelta(invoice.cogs_delta)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onBack}
                disabled={busy}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
              >
                Back
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={busy || hasBlockers}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isConfirming ? 'Saving...' : 'Confirm correction'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartingValueCostCorrectionModal;
